import { getVapidPublicKey, subscribeToPush, unsubscribeFromPush } from './api';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

export function detectDeviceType() {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/.test(ua)) return 'iPhone';
  if (/Android/.test(ua)) return 'Android';
  return 'Desktop';
}

export function isIos() {
  return /iPhone|iPad|iPod/.test(navigator.userAgent || '');
}

// True once the PWA is actually installed/launched standalone — iOS only allows
// the Notification permission prompt from a standalone-launched app.
export function isStandalone() {
  return window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
}

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Requests permission, subscribes via the active service worker's PushManager,
// and saves the subscription server-side. Returns { ok, reason }.
export async function enablePushNotifications(userId) {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  if (isIos() && !isStandalone()) return { ok: false, reason: 'ios-not-installed' };

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'denied' };

  try {
    const registration = await navigator.serviceWorker.ready;
    const { publicKey } = await getVapidPublicKey();
    if (!publicKey) return { ok: false, reason: 'no-vapid-key' };

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    await subscribeToPush({
      user_id: userId,
      subscription: subscription.toJSON(),
      device_label: detectDeviceType(),
    });
    return { ok: true };
  } catch (err) {
    console.error('Failed to enable push notifications:', err);
    return { ok: false, reason: 'error' };
  }
}

// Unsubscribes this device entirely (used by "Disable all notifications").
export async function disablePushNotifications() {
  if (!isPushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await unsubscribeFromPush(subscription.endpoint);
      await subscription.unsubscribe();
    }
  } catch (err) {
    console.error('Failed to disable push notifications:', err);
  }
}
